import { useCallback } from 'react';
import moment from 'moment';
import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';

const fetchRepositoryData = async ({
  proxyUrl,
  token,
  pathname,
}: {
  proxyUrl: string;
  token?: string;
  pathname: string;
}) => {
  const response = await fetch(`${proxyUrl}/repos${pathname}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response.json();
  return data;
};

const fetchPullRequestsData = async ({
  proxyUrl,
  token,
  pathname,
}: {
  proxyUrl: string;
  token?: string;
  pathname: string;
}) => {
  const response = await fetch(`${proxyUrl}/repos${pathname}/pulls`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response.json();
  return data;
};

const fetchCommitsData = async ({
  proxyUrl,
  token,
  pathname,
  branch,
}: {
  proxyUrl: string;
  token?: string;
  pathname: string;
  branch: string;
}) => {
  const response = await fetch(
    `${proxyUrl}/repos${pathname}/commits/${branch}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  const data = await response.json();
  return data;
};

const fetchReleasesData = async ({
  proxyUrl,
  token,
  pathname,
}: {
  proxyUrl: string;
  token?: string;
  pathname: string;
}) => {
  const response = await fetch(`${proxyUrl}/repos${pathname}/releases`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response.json();

  if (!data.length) return null;

  return data;
};

export function useGithubApi() {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const fetchResponse = useCallback(
    async (pathname: string) => {
      if (!pathname) {
        throw new Error('Pathname is not provided');
      }

      const currentDate = moment();

      const proxyBaseUrl = await discoveryApi.getBaseUrl('proxy');
      const proxyUrl = `${proxyBaseUrl}/github-api`;

      const { token } = await identityApi.getCredentials();

      const repoData = await fetchRepositoryData({ proxyUrl, token, pathname });
      const [commitsData, releasesData, pullRequestsData] = await Promise.all([
        fetchCommitsData({
          proxyUrl,
          token,
          pathname: pathname,
          branch: repoData.default_branch,
        }),
        fetchReleasesData({ proxyUrl, token, pathname }),
        fetchPullRequestsData({ proxyUrl, token, pathname }),
      ]);

      const lastCommitDate = moment(commitsData.commit.committer.date);
      const daysSinceLastCommit = currentDate.diff(lastCommitDate, 'days');
      const openPullRequests = pullRequestsData.length

      let daysSinceLastRelease = null;

      if (Array.isArray(releasesData) && releasesData.length) {
        const latestRelease = releasesData[0];
        const latestReleaseDate = moment(latestRelease.published_at);
        daysSinceLastRelease = currentDate.diff(latestReleaseDate, 'days');
      }

      return {
        daysSinceLastCommit,
        daysSinceLastRelease,
        openPullRequests,
        stargazers: repoData.stargazers_count,
        subscribers: repoData.subscribers_count,
      };
    },
    [discoveryApi, identityApi],
  );

  return fetchResponse;
}
